import { render } from "dom-serializer";
import { AnyNode, Document, Element, Text } from "domhandler";
import {
  find,
  findAll,
  findOne,
  getAttributeValue,
  isTag,
  removeElement,
  replaceElement,
  textContent,
} from "domutils";
import JSZip, { JSZipObject } from "jszip";
import { v4 as uuid } from "uuid";

import { Cardinality } from "@/generated/graphql";

import { getParsedXML, patchMath } from "../utils";
import { convertQTIQuestion } from "./common";
import {
  ConditionVar,
  QTIFeedback,
  QTIFeedbackShowHide,
  QTIFeedbackType,
  QTIMaterial,
  QTIOutcomes,
  QTIPrompt,
  QTIPromptImage,
  QTIResponse,
  QTIResponseCondition,
  QTIResponseConditionVar,
} from "./types";

/**
 * Read file given as a file ref and parse the content and
 **/
async function parseAssessmentItemFileRef(
  filepath: string,
  file: JSZipObject,
  zip: JSZip
) {
  const lastSlashIndex = file.name.lastIndexOf("/");
  const path = file.name.substring(0, lastSlashIndex);
  const xmlContent = await zip.file([path, filepath].join("/"))?.async("text");
  return xmlContent
    ? findOne(
        (e) => e.tagName === "assessmentItem",
        getParsedXML(xmlContent).childNodes
      )
    : null;
}

// Map response's element rcardinality attribute to Cardinality enum
function getResponseCardinality(response: Element) {
  const rawCardinality =
    getAttributeValue(response, "rcardinality") ??
    getAttributeValue(response, "cardinality");
  return getCardinality(rawCardinality);
}

/**
 * Map cardinality from QTI file to Cardinality enum
 **/
function getCardinality(cardinalityString?: string) {
  switch (cardinalityString?.toLowerCase()) {
    case "multiple": {
      return Cardinality.Multiple;
    }
    case "ordered": {
      return Cardinality.Ordered;
    }
    case "single":
    default:
      return Cardinality.Single;
  }
}

/**
 * Parse QTI response(s) element to a QTIResponse type
 * with its cardinality, type, choices and related settings
 **/
function parseResponses(rawQuestion: Element) {
  return findAll(
    (e) => isTag(e) && e.tagName === "responseDeclaration",
    rawQuestion.childNodes
  ).map((e) => {
    const id = getAttributeValue(e, "identifier") ?? uuid();
    let cardinality = getResponseCardinality(e);
    const type = getAttributeValue(e, "baseType") ?? "string";
    const response: QTIResponse = {
      id,
      cardinality,
      type,
    };
    const correctResponse = findOne(
      (e) => e.tagName === "correctResponse",
      e.childNodes
    );
    if (correctResponse) {
      const values = findAll(
        (e) => e.tagName === "value",
        correctResponse.childNodes
      );
      if (values) {
        response.correctResponses = values.map((v) => textContent(v));
      }
    }
    const mapping = findOne((e) => e.tagName === "mapping", e.childNodes);
    if (mapping) {
      const defaultVal = getAttributeValue(mapping, "defaultValue");
      const lower = getAttributeValue(mapping, "lowerBound");
      const upper = getAttributeValue(mapping, "upperBound");
      const entries = findAll(
        (e) => e.tagName === "mapEntry",
        mapping.childNodes
      );
      if (entries) {
        response.mapping = {
          default: defaultVal,
          lower,
          upper,
          entries: entries.map((v) => ({
            key: getAttributeValue(v, "mapKey")!,
            value: getAttributeValue(v, "mappedValue")!,
          })),
        };
      }
    }
    const interaction = findOne(
      (e) =>
        /interaction/gi.test(e.tagName) &&
        getAttributeValue(e, "responseIdentifier") === id,
      rawQuestion.childNodes,
      true
    );
    if (interaction) {
      response.interactionType = interaction.tagName;
    }

    const expectedLength =
      interaction && getAttributeValue(interaction, "expectedLength");
    response.wordLimit = expectedLength
      ? parseInt(expectedLength, 10)
      : undefined;

    if (
      /choice|associate|match|hotspot/gi.test(interaction?.tagName ?? "") &&
      interaction?.childNodes
    ) {
      const shuffle = /true/gi.test(
        getAttributeValue(interaction, "shuffle") ?? ""
      );
      const maxChoiceAttrs = getAttributeValue(interaction, "maxChoices");
      const maxChoices = maxChoiceAttrs
        ? parseInt(maxChoiceAttrs, 10)
        : undefined;
      if (maxChoices && maxChoices > 1) {
        cardinality = Cardinality.Multiple;
      }
      response.choiceResponse = {
        choices: findAll(
          (e) => /choice|gapText/gi.test(e.tagName),
          interaction?.childNodes
        ).map((e) => ({
          id: getAttributeValue(e, "identifier") ?? uuid(),
          fixed: /true/gi.test(getAttributeValue(e, "fixed") ?? ""),
          contents: e.childNodes.map(
            (e) =>
              ({
                type: "text",
                textType: "html",
                text: textContent(e) ?? "",
              }) satisfies QTIMaterial
          ),
          shape: getAttributeValue(e, "shape"),
          coords: getAttributeValue(e, "coords")?.split(",").map(Number),
        })),
        shuffle,
        maxChoices,
      };
    }

    return response;
  });
}

const inlineInteractionsTags: readonly string[] = [
  "inlineChoiceInteraction",
  "textEntryInteraction",
  "gap",
] as const;

const interactionContentsTags: readonly string[] = [
  "gapText",
  "simpleChoice",
  "inlineChoice",
  "simpleAssociableChoice",
  "simpleMatchSet",
  "hotspotChoice",
  "associableHotspot",
  "object",
  "positionObjectInteraction",
] as const;

const hotspotInteractionTags: readonly string[] = [
  "hotspotInteraction",
  "graphicAssociateInteraction",
  "selectPointInteraction",
  "positionObjectStage",
];

/**
 * Replace an inline interaction to a FITB response element
 **/
function patchInlineInteraction(contents: AnyNode[]) {
  const inlineInteractions = findAll(
    (e) => isTag(e) && inlineInteractionsTags.includes(e.tagName),
    contents
  );
  inlineInteractions.forEach((e) => {
    const id =
      getAttributeValue(e, "responseIdentifier") ??
      getAttributeValue(e, "identifier");
    if (id) {
      replaceElement(
        e,
        new Element("span", { "data-cadmus-blank": "0", id: id }, [
          new Text("____"),
        ])
      );
    } else {
      removeElement(e);
    }
  });
  return contents;
}

/**
 * Remove FeedbackInteraction elements from QTI Question prompt's contents
 **/
function patchFeedbackInteraction(contents: AnyNode[]) {
  findAll(
    (e) => isTag(e) && /feedback|interaction/gi.test(e.tagName),
    contents
  ).forEach((e) => removeElement(e));
  return contents;
}

function parsePrompts(itemBody: Element): QTIPrompt[] {
  return findAll(
    (e) =>
      !!e.parent &&
      isTag(e.parent) &&
      hotspotInteractionTags.includes(e.parent.tagName) &&
      !interactionContentsTags.includes(e.tagName),
    itemBody?.cloneNode?.(true)?.childNodes ?? []
  ).flatMap((e) =>
    e.parent &&
    isTag(e.parent) &&
    getAttributeValue(e.parent, "responseIdentifier")
      ? {
          responseId: getAttributeValue(e.parent, "responseIdentifier")!,
          contents: [
            {
              type: "text",
              textType: "html",
              text: render(e.children),
            },
          ],
        }
      : []
  );
}

function parseContents(itemBody: Element) {
  const contents = find(
    (e) => isTag(e) && !/interaction|feedback|stage/gi.test(e.tagName),
    itemBody?.cloneNode?.(true)?.childNodes ?? [],
    false,
    Number.MAX_SAFE_INTEGER
  );
  const prompts = findAll(
    (e) =>
      !!e.parent &&
      isTag(e.parent) &&
      /interaction/gi.test(e.parent.tagName) &&
      (contents.length
        ? !hotspotInteractionTags.includes(e.parent.tagName)
        : /interaction/gi.test(e.parent.tagName)) &&
      !interactionContentsTags.includes(e.tagName),
    itemBody?.cloneNode?.(true)?.childNodes ?? []
  ).flatMap((e) => {
    // make sure that there is no duplicate between contents and prompts
    if (
      !contents.length ||
      !contents.map((c) => render(c).includes(render(e)))
    ) {
      return [new Element("p", {}, e.childNodes)];
    }
    return [];
  });

  return patchFeedbackInteraction(
    patchInlineInteraction(patchMath([...contents, ...prompts]))
  ).map((e) => ({
    type: "text",
    textType: "html",
    text: render(e),
  }));
}

const matchCorrectTemplate = `
<responseProcessing xmlns="http://www.imsglobal.org/xsd/imsqti_v2p2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqti_v2p2 http://www.imsglobal.org/xsd/qti/qtiv2p2/imsqti_v2p2.xsd">
<responseCondition>
<responseIf>
<match>
<variable identifier="RESPONSE"/>
<correct identifier="RESPONSE"/>
</match>
<setOutcomeValue identifier="SCORE">
<baseValue baseType="float">1</baseValue>
</setOutcomeValue>
</responseIf>
<responseElse>
<setOutcomeValue identifier="SCORE">
<baseValue baseType="float">0</baseValue>
</setOutcomeValue>
</responseElse>
</responseCondition>
</responseProcessing>
` as const;

const mapResponseTemplate =
  `This XML file does not appear to have any style information associated with it. The document tree is shown below.
<responseProcessing xmlns="http://www.imsglobal.org/xsd/imsqti_v2p2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqti_v2p2 http://www.imsglobal.org/xsd/qti/qtiv2p2/imsqti_v2p2.xsd">
<responseCondition>
<responseIf>
<isNull>
<variable identifier="RESPONSE"/>
</isNull>
<setOutcomeValue identifier="SCORE">
<baseValue baseType="float">0.0</baseValue>
</setOutcomeValue>
</responseIf>
<responseElse>
<setOutcomeValue identifier="SCORE">
<mapResponse identifier="RESPONSE"/>
</setOutcomeValue>
</responseElse>
</responseCondition>
</responseProcessing>` as const;

const mapResponsePointTemplate =
  `<responseProcessing xmlns="http://www.imsglobal.org/xsd/imsqti_v2p2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.imsglobal.org/xsd/imsqti_v2p2 http://www.imsglobal.org/xsd/qti/qtiv2p2/imsqti_v2p2.xsd">
<responseCondition>
<responseIf>
<isNull>
<variable identifier="RESPONSE"/>
</isNull>
<setOutcomeValue identifier="SCORE">
<baseValue baseType="float">0</baseValue>
</setOutcomeValue>
</responseIf>
<responseElse>
<setOutcomeValue identifier="SCORE">
<mapResponsePoint identifier="RESPONSE"/>
</setOutcomeValue>
</responseElse>
</responseCondition>
</responseProcessing>` as const;

/**
 * Parse respConditions element coming from either a response template or inlined
 **/
function getRespConditions(parent: Element, template?: string) {
  let root: Element | Document;
  switch (template) {
    case "http://www.imsglobal.org/question/qti_v2p2/rptemplates/match_correct.xml":
      root = getParsedXML(matchCorrectTemplate);
      break;
    case "http://www.imsglobal.org/question/qtiv2p2/rptemplates/map_response.xml":
      root = getParsedXML(mapResponseTemplate);
      break;
    case "http://www.imsglobal.org/question/qtiv2p2/rptemplates/map_response_point.xml":
      root = getParsedXML(mapResponsePointTemplate);
      break;
    default:
      root = parent;
  }
  return find(
    (e) => isTag(e) && e.tagName === "responseCondition",
    root.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  );
}

const operatorTagNames: readonly string[] = [
  "anyN",
  "equal",
  "equalRounder",
  "customOperator",
  "fieldValue",
  "index",
  "inside",
  "multiple",
  "ordered",
  "and",
  "gcd",
  "lcm",
  "min",
  "max",
  "or",
  "product",
  "contains",
  "delete",
  "divide",
  "durationgte",
  "durationlt",
  "gt",
  "gte",
  "integerDivide",
  "integerModulus",
  "lt",
  "lte",
  "match",
  "member",
  "power",
  "subtract",
  "containerSize",
  "integerToFloat",
  "isNull",
  "not",
  "random",
  "round",
  "truncate",
  "mathOperator",
  "sum",
  "patternMatch",
  "repeat",
  "roundTo",
  "statsOperator",
  "stringMatch",
  "substring",
];

// Get all direct children element that is considered as operators
function getOperatorElements(parent: Element): Element[] {
  return find(
    (e) => isTag(e) && operatorTagNames.includes(e.tagName),
    parent.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
}

/**
 * Recursively parsed nested response conditions' conditions
 **/
function parseConditionOperators(
  conditionElement: Element,
  existingConditions?: ConditionVar[]
): Partial<QTIResponseCondition>[] | undefined {
  const operatableElements =
    conditionElement.tagName !== "responseElse"
      ? getOperatorElements(conditionElement)
      : [conditionElement];
  return operatableElements.flatMap((e) => {
    const element = e as Element;
    const tagName = element.tagName.toLowerCase();
    const operators = getOperatorElements(element);
    const conditions: ConditionVar[] = [];
    const extraConditions: QTIResponseCondition[] = [];
    const respConditions = getRespConditions(conditionElement);
    if (operators?.length) {
      conditions.push({
        operation: tagName,
        conditions: parseConditionOperators(element, existingConditions),
      } satisfies ConditionVar);
    } else {
      const variable = findOne((e) => e.tagName === "variable", e.childNodes);
      if (!variable && existingConditions?.length) {
        conditions.push({
          operation: ["responseIf", "responseElseIf"].includes(e.tagName)
            ? "and"
            : "not",
          conditions: existingConditions,
        } satisfies ConditionVar);
      } else if (variable) {
        const id = getAttributeValue(variable, "identifier");
        if (!id) return [];
        const condition: ConditionVar = {
          condition: {
            comparator: tagName,
            id,
          },
        };
        const baseValue = findOne(
          (e) => e.tagName === "baseValue",
          e.childNodes
        );
        if (baseValue && condition.condition) {
          condition.condition.value = textContent(baseValue);
          condition.condition.valueType =
            getAttributeValue(baseValue, "baseType") ?? "string";
        }
        const correct = findOne((e) => e.tagName === "correct", e.childNodes);
        if (correct && condition.condition) {
          condition.condition.value = getAttributeValue(correct, "identifier");
          condition.condition.valueType = "correct";
        }
        // This seems to keep trying to and/or on existing matching pairs
        //  which are irrelevant to each other.
        if (existingConditions?.length && e.tagName !== "match") {
          conditions.push({
            operation: "and",
            conditions: [
              {
                operation: ["responseIf", "responseElseIf"].includes(e.tagName)
                  ? "and"
                  : "not",
                conditions: existingConditions,
              },
              condition,
            ],
          });
        } else {
          conditions.push(condition);
        }
      } else if (existingConditions?.length) {
        conditions.push({
          operation: ["responseIf", "responseElseIf"].includes(e.tagName)
            ? "and"
            : "not",
          conditions: existingConditions,
        });
      }
    }
    if (respConditions?.length) {
      parseResponseConditionsElements(respConditions, conditions).map((c) => {
        extraConditions.push(c);
      });
    }
    if (extraConditions.length) {
      return [
        { conditions },
        ...extraConditions.map((con) => {
          return {
            vars: con.vars,
            conditions: [
              {
                operation: "and",
                conditions: [...(con.conditions ?? []), ...conditions],
              },
            ],
          };
        }),
      ];
    }
    return [
      {
        conditions,
      },
    ];
  });
}

/**
 * Parse variables that get set as part of outcome values
 **/
function parseResponseOutcomeVariables(
  outcomeValue: Element,
  random: boolean = false,
  name?: string
): QTIResponseConditionVar[] {
  let randomised = random;
  let multiple = find(
    (e) => isTag(e) && e.tagName === "multiple",
    outcomeValue.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  );
  const randomValue = findOne(
    (e) => e.tagName === "random",
    outcomeValue.childNodes,
    false
  );
  if (randomValue) {
    multiple = find(
      (e) => isTag(e) && e.tagName === "multiple",
      randomValue.childNodes,
      false,
      Number.MAX_SAFE_INTEGER
    );
    if (multiple.length) {
      randomised = true;
    }
  }
  if (multiple.length) {
    return multiple.flatMap((m) =>
      isTag(m) ? parseResponseOutcomeVariables(m, randomised, name) : []
    );
  }
  const baseValue = find(
    (e) => isTag(e) && e.tagName === "baseValue",
    outcomeValue.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  );
  if (baseValue?.length) {
    return baseValue.flatMap((b) => {
      if (!isTag(b)) return [];
      return [
        {
          name: name ?? getAttributeValue(b, "identifier") ?? "SCORE",
          action: randomised ? "random" : "set",
          actionType: getAttributeValue(b, "baseType"),
          actionIdentifier: getAttributeValue(b, "identifier"),
          value: textContent(b),
        },
      ];
    });
  }
  const outcomeVariables = find(
    (e) => isTag(e) && /^mapResponse|variable/gi.test(e.tagName),
    outcomeValue.childNodes,
    false,
    Number.MAX_SAFE_INTEGER
  );
  return outcomeVariables.flatMap((b) => {
    if (!isTag(b)) return [];
    return [
      {
        name: name ?? getAttributeValue(b, "identifier") ?? "SCORE",
        action: randomised ? "random" : "set",
        actionType: b.tagName,
        actionIdentifier: getAttributeValue(b, "identifier"),
      },
    ];
  });
}

/**
 * Recursively parse response conditions element to
 * add proper conditions for else if and else conditions
 * based on prior if conditions
 **/
function parseResponseConditionsElements(
  conditionsElements: AnyNode[] = [],
  existingConditions: ConditionVar[] = []
): QTIResponseCondition[] {
  return conditionsElements?.flatMap((r) => {
    if (!isTag(r) || !r?.childNodes) return [];
    const conditionElements = find(
      (e) =>
        isTag(e) &&
        ["responseIf", "responseElseIf", "responseElse"].includes(e.tagName),
      r.childNodes,
      false,
      Number.MAX_SAFE_INTEGER
    );
    let priorConditions: ConditionVar[] = existingConditions;
    return conditionElements.flatMap((condition) => {
      if (!isTag(condition)) return [];
      const setVars = find(
        (e) => isTag(e) && e.tagName === "setOutcomeValue",
        condition.childNodes,
        false,
        Number.MAX_SAFE_INTEGER
      );
      const conditions = parseConditionOperators(condition, priorConditions);
      priorConditions = conditions?.[0]?.conditions ?? [];
      return (
        conditions?.flatMap((c) => {
          if (c.vars?.length) return [c as QTIResponseCondition];
          else if (!c.vars && setVars.length) {
            return [
              {
                vars: setVars?.flatMap((v) => {
                  if (!isTag(v)) return [];
                  return parseResponseOutcomeVariables(
                    v,
                    false,
                    getAttributeValue(v, "identifier") ?? "SCORE"
                  );
                }),
                conditions: c.conditions,
              },
            ];
          }
          return [];
        }) ?? []
      );
    });
  });
}

/**
 * Parse response conditions
 **/
function parseRespConditions(rawQuestion: Element) {
  const responseProcessing = findOne(
    (e) => e.tagName === "responseProcessing",
    rawQuestion.childNodes,
    true
  );
  if (!responseProcessing) return [];
  const template = getAttributeValue(responseProcessing, "template");
  const respConditionsElements = getRespConditions(
    responseProcessing,
    template
  );
  return parseResponseConditionsElements(respConditionsElements, []);
}

// Parse number type outcomes to a float/integer based on its type
function parseNumberOutcome(type: string, value?: string): number | undefined {
  if (!value) return;
  switch (type) {
    case "float": {
      return parseFloat(value);
    }
    case "integer": {
      return parseInt(value, 10);
    }
    default: {
      return;
    }
  }
}
/**
 * Parse outcome variables, their type, cardinality, and value range/defaults
 **/
function parseOutcomes(rawQuestion: Element) {
  return findAll(
    (e) => e.tagName === "outcomeDeclaration",
    rawQuestion.childNodes
  ).reduce((outcomes, outcome) => {
    const type = getAttributeValue(outcome, "baseType");
    const id = getAttributeValue(outcome, "identifier");
    const cardinality = getAttributeValue(outcome, "cardinality");
    const min = getAttributeValue(outcome, "normalMinimum");
    const max = getAttributeValue(outcome, "normalMaximum");
    const masteryValue = getAttributeValue(outcome, "masteryValue");
    if (id && type && cardinality) {
      outcomes[id] = {
        type,
        cardinality: getCardinality(cardinality),
      };
      if (["float", "integer"].includes(type)) {
        outcomes[id]!.cut = parseNumberOutcome(type, masteryValue);
        outcomes[id]!.min = parseNumberOutcome(type, min);
        outcomes[id]!.max = parseNumberOutcome(type, max);
      }
      const defaultValues = findAll(
        (e) =>
          !!e.parent &&
          isTag(e.parent) &&
          e.parent.tagName === "defaultValue" &&
          e.tagName === "value",
        outcome.childNodes
      );
      if (
        cardinality === Cardinality.Single &&
        defaultValues?.length === 1 &&
        defaultValues[0]
      ) {
        const value = textContent(defaultValues[0] ?? []);
        outcomes[id]!.default = parseNumberOutcome(type, value) ?? value;
      } else {
        outcomes[id]!.default =
          defaultValues.length > 0
            ? defaultValues.map((v) => {
                const value = textContent(v ?? []);
                return parseNumberOutcome(type, value) ?? value;
              })
            : undefined;
      }
    }
    return outcomes;
  }, {} as QTIOutcomes);
}

/**
 * Parse feedbacks element with its type, contents, identifiers
 * that controls its visibility
 **/
function parseFeedbacks(rawQuestion: Element): QTIFeedback[] {
  const feedbackElements = find(
    (e) => isTag(e) && /feedback/gi.test(e.tagName),
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  );
  if (!feedbackElements.length) return [];
  return feedbackElements.flatMap((e) => {
    if (!isTag(e)) return [];
    const identifier = getAttributeValue(e, "identifier");
    if (!identifier) return [];
    const outcomeIdentifier = getAttributeValue(e, "outcomeIdentifier");
    const contents = parseContents(e);
    const showHideAttribute = getAttributeValue(e, "showHide");
    let showHide: QTIFeedback["showHide"];
    let type: QTIFeedback["type"];
    switch (e.tagName) {
      case "feedbackInline":
        type = QTIFeedbackType.inline;
        break;
      case "feedbackBlock":
        type = QTIFeedbackType.block;
        break;
      case "modalFeedback":
        type = QTIFeedbackType.modal;
        break;
    }
    switch (showHideAttribute) {
      case "show":
        showHide = QTIFeedbackShowHide.show;
        break;
      case "hide":
        showHide = QTIFeedbackShowHide.hide;
        break;
    }
    return [
      {
        id: identifier,
        type,
        contents,
        outcomeId: outcomeIdentifier,
        showHide,
      },
    ];
  });
}

function parsePromptImage(rawQuestion: Element): QTIPromptImage | undefined {
  const hotspot = findOne(
    (e) => isTag(e) && hotspotInteractionTags.includes(e.tagName),
    rawQuestion.childNodes
  );
  if (!hotspot) return;
  const image = findOne(
    (e) => isTag(e) && e.tagName === "object",
    hotspot.childNodes
  );
  if (!hotspot) return;
  if (!image) return;
  return {
    attributes: {
      width: image.attribs.width,
      height: image.attribs.height,
      uri: image.attribs.href ?? image.attribs.data,
    },
    content: textContent(image),
  };
}

/**
 * Parse assessment item's itemBody to a QTIQuestion
 **/
async function parseAssessmentItem(
  root: AnyNode,
  file?: JSZipObject | null,
  zip?: JSZip
) {
  const rawQuestion = root as Element;
  const itemBody = findOne(
    (e) => e.tagName === "itemBody",
    rawQuestion.childNodes,
    true
  );
  if (!itemBody) return [];
  const contents = parseContents(itemBody);
  const responses = parseResponses(rawQuestion);
  const respConditions = parseRespConditions(rawQuestion);
  const outcomes = parseOutcomes(rawQuestion);
  const feedbacks = parseFeedbacks(rawQuestion);
  const promptImage = parsePromptImage(rawQuestion);
  const prompts = parsePrompts(rawQuestion);
  const question = await convertQTIQuestion(
    {
      contents,
      prompts,
      promptImage,
      responses,
      metadata: {},
      feedbacks,
      outcomes,
      respConditions,
    },
    file,
    zip
  );
  if (question) return [question];
  return [];
}

/**
 * Parse questions from an assessmentItemRef from a qti file
 **/
async function parseAssessmentItemRef(
  root: AnyNode,
  file?: JSZipObject | null,
  zip?: JSZip
) {
  if (!isTag(root)) return [];
  const fileRef = getAttributeValue(root, "href");
  if (!fileRef || !file || !zip) return [];
  const assessmentItemFile = await parseAssessmentItemFileRef(
    fileRef,
    file,
    zip
  );
  if (!assessmentItemFile) return [];
  return await parseAssessmentItem(assessmentItemFile, file, zip);
}

/**
 * Parse questions from QTI xml, whether it is inlined assessment item
 * or a ref to a file that has the assessment item
 **/
async function parseQuestions(
  parsedXML: Document,
  file?: JSZipObject | null,
  zip?: JSZip
) {
  const assessmentItems = find(
    (e) =>
      isTag(e) &&
      (e.tagName === "assessmentItem" || e.tagName === "assessmentItemRef"),
    parsedXML.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  );
  if (!assessmentItems) return [];
  return Promise.all(
    assessmentItems.flatMap(async (e) => {
      if (isTag(e) && e.tagName === "assessmentItem") {
        return await parseAssessmentItem(e, file, zip);
      }
      return await parseAssessmentItemRef(e, file, zip);
    })
  );
}

export const parser = {
  parseQuestions,
};
